import { AxisAlignedBoundingBox, BoundingRectangle, Math as CMath, Cartesian2, Cartesian3, Cartographic, Color, createGuid, HeadingPitchRange, Ray, } from "cesium";
import { HeightMap } from "../../core/height-map";
import { OBJECT_CELL_SIZE, } from "../../settings/earthbrain-site-settings";
import { AssetType, CESIUM_PICKABLE_ENTITY, EmbankmentType, } from "../../types";
import { Activity, ActivityLogger } from "../../utilities/activity-logger";
import { EarthBrainUtils } from "../../utilities/earthbrainutility";
import { getRaycastableFromRubbingDestination } from "../../utilities/structure-utilities";
import { PointType, } from "../arbitrary-line/types";
import { BendType } from "../mid-path/interface";
import { Site } from "../site";
import { ConstructionState } from "../trench/earthbrain-trench";
import { DEFAULT_ROAD_SETTING, DEFAULT_TERRIAN_PROFILE_VIEW_STEP_SIZE, DEFAULT_TERRIAN_SECTION_VIEW_STEP_SIZE, } from "./defaults";
import { RoadGeometry } from "./road-geometry/road-geometry";
import { Alignment } from "../../core/alignment/alignment";
import { ZBoundedPlane } from "../../core/z-bounded-plane";
import { intersectGeometryByPlane } from "../../utilities/geometry-utility";
import { linkPvisToIps } from "../../core/alignment/vertical-alignment/pvi-ip-link";
import { CesiumRenderableAlignment } from "../alignment/cesium-renderable-alignment";
import { toSectionMarkers } from "../alignment/alignment-section-marker-adapter";
//image is used to pick points when constructing or editing roads, and it is going to rub against the pointcloud. If there is no pointcloud than its going torub against the image
export class Road {
    assetType = AssetType.Road_Data;
    // -----------private variables-----------
    _verticalAlignmentData;
    _wgs84dataMemo;
    _id;
    _constructionState;
    // polylines
    _roadSettings;
    _roadGeometry;
    _viewer;
    _2dDataMemo;
    _associatedTerrain;
    _rubbingDestination;
    _heightMap = undefined;
    _alignment;
    _cesiumRenderableAlignment;
    _isMagnified = false;
    //RENDER Entities
    _renderData;
    // DEBUG Entities
    _debugEntities = [];
    /**
     *
     * @param viewer
     * @param id
     * @param roadSettings
     * @param rubbingDestination
     * @param associatedTerrain
     * @param slopeBreaks : All slope breaks are considered active
     */
    constructor(viewer, id, roadSettings, rubbingDestination, associatedTerrain) {
        this._id = id;
        this._roadSettings = roadSettings || DEFAULT_ROAD_SETTING;
        this._rubbingDestination = rubbingDestination;
        this._associatedTerrain = associatedTerrain;
        this._viewer = viewer;
        this._verticalAlignmentData = [];
        // this._curvePolylines = [];
        this._roadGeometry = new RoadGeometry(this._id);
        this._alignment = new Alignment(this._id);
        this._2dDataMemo = {
            altitudeViewData: {
                designDataOutlines: [],
                pathPoints: [],
                pointCloudOutlines: [],
                referenceTopologyOutlines: [],
                sectionLineDistFromFirstPoint: 0,
                slopeBreaks: [],
                sectionMarkers: [],
            },
            sectionViewData: {
                designDataOutlines: [],
                pathPoints: [],
                pointCloudOutlines: [],
                referenceTopologyOutlines: [],
                referencePointIndex: 0,
            },
            distanceFromStart: 0,
        };
        this._constructionState = ConstructionState.NONE;
        this._wgs84dataMemo = { geometries: [] };
        this._renderData = {
            pviSpheres: new Map(),
            geometryPrimitives: [],
        };
        this._cesiumRenderableAlignment = new CesiumRenderableAlignment(this._alignment, this._viewer, {
            width: this._roadSettings.commonSetting.path.controlLineSettings.width,
            color: this._roadSettings.commonSetting.path.controlLineSettings.color,
            outlineWidth: this._roadSettings.commonSetting.path.controlLineSettings
                .outlineWidth,
            outlineColor: this._roadSettings.commonSetting.path.controlLineSettings
                .outlineColor,
        });
    }
    // --------------Getters and Setters--------------
    get geometry() {
        return this._roadGeometry.geometry;
    }
    get verticalAlignmentData() {
        return this._verticalAlignmentData;
    }
    get isMagnified() {
        return this._isMagnified;
    }
    set isMagnified(_val) {
        this._isMagnified = _val;
    }
    set constructionState(state) {
        this._constructionState = state;
    }
    get constructionState() {
        return this._constructionState;
    }
    get associatedTerrain() {
        return this._associatedTerrain;
    }
    get associatedRubbingDestination() {
        return this._rubbingDestination;
    }
    get controlPointLength() {
        return this._alignment.ipLinePoints.length;
    }
    get id() {
        return this._id;
    }
    set id(newID) {
        this._id = newID;
    }
    get controlPoints() {
        return this._alignment.ipLinePoints;
    }
    get settings() {
        return this._roadSettings;
    }
    get heightMap() {
        return this._heightMap;
    }
    get alignment() {
        return this._alignment;
    }
    get cesiumRenderableAlignment() {
        return this._cesiumRenderableAlignment;
    }
    // ----------------class methods----------------
    static async fromJSON(roadData, cesiumViewer, stationInterval) {
        const site = Site.getInstance();
        const associatedTerrain = site.getTerrain(roadData.associatedTerrain.id, roadData.associatedTerrain.terrainType);
        const roadUnderConstruction = new Road(cesiumViewer.viewer, roadData.roadId, roadData.roadSettings, roadData.rubbingDestination, associatedTerrain);
        roadUnderConstruction.id = roadData.roadId;
        await roadUnderConstruction.initializeRoadFromAlignmentData(roadData.surveyPoints, roadData.ipPointIds, roadData.horizontalAlignment, roadData.verticalAlignment, stationInterval);
        return roadUnderConstruction;
    }
    static FromControlPoints(controlPointArray, viewer, roadSettings, rubbingDestination, associatedTerrain) {
        const site = Site.getInstance();
        const road = new Road(viewer, createGuid(), roadSettings, rubbingDestination, associatedTerrain);
        const controlPoints = [];
        for (let iter = 0; iter < controlPointArray.length; iter += 3) {
            controlPoints.push({
                point: new Cartesian3(controlPointArray[iter], controlPointArray[iter + 1], controlPointArray[iter + 2]),
                bendType: BendType.NONE,
            });
        }
        const tileSet = getRaycastableFromRubbingDestination(site, rubbingDestination);
        road._roadGeometry.update(road._alignment, roadSettings, tileSet);
        road._constructionState = ConstructionState.READY;
        return road;
    }
    static async FromControlPointsWithAI(roadPoints, controlPointArray, viewer, roadSettings, rubbingDestination, associatedTerrain) {
        // roadPoints（ローカル座標）と controlPointArray（WGS84）は同じ点列の別表現であり、
        // 呼び出し側が同数で渡す前提に依存している。
        if (roadPoints.length !== controlPointArray.length) {
            throw new Error("roadPoints and controlPointArray must have the same length");
        }
        const site = Site.getInstance();
        const road = new Road(viewer, createGuid(), roadSettings, rubbingDestination, associatedTerrain);
        // AIサーバのレスポンスからControlPointを追加
        const pointInfos = [];
        road._constructionState = ConstructionState.UNDER_CONSTRUCTION;
        for (let iter = 0; iter < roadPoints.length; iter += 3) {
            pointInfos.push({
                position: new Cartesian3(controlPointArray[iter], controlPointArray[iter + 1], controlPointArray[iter + 2]),
                localPosition: new Cartesian3(roadPoints[iter], roadPoints[iter + 1], roadPoints[iter + 2]),
                info: undefined,
            });
        }
        const tileSet = getRaycastableFromRubbingDestination(site, rubbingDestination);
        // 逐次 await しないと READY 遷移が先行し、各点の継続処理が
        // updateRoadGeometry を並行実行してしまうため必ず直列化する。
        // ただし 1 点ごとの再描画は全中心線点の WGS84 変換（Worker 往復）を伴い
        // 点数に対して二乗的に膨らむため、deferRender で抑止してループ後に一度だけ描画する。
        for (const pointInfo of pointInfos) {
            await road.addIPPointAtEnd(pointInfo, { deferRender: true });
        }
        // AI は各測点の高さを返すが、末尾追加フローでは縦断が始終点 2 点に潰れる。
        // AI の縦断を保持するため、全 IP 点の z から縦断プロファイルを再構築する。
        road._alignment.setVerticalProfileFromIPHeights();
        // 生成された BC / EC は水平線形の 2D 計算で z が 0 のままのため、
        // 縦断から高さを補完して保存データ（surveyPoints）にも反映する。
        await road._cesiumRenderableAlignment.applyGeneratedSurveyPointHeights();
        // 高さの確定後に一度だけ描画する（補正前の高さで球やラベルを描かないため）。
        await road._cesiumRenderableAlignment.render();
        road._roadGeometry.update(road._alignment, roadSettings, tileSet);
        road._constructionState = ConstructionState.READY;
        return road;
    }
    // ---------------Public methods---------------
    isVisible() {
        // if any of the primitive, or alignment is visible, it is considered visible
        const isAlignmentVisible = this._cesiumRenderableAlignment.isVisible();
        const isAnyPrimitiveVisible = this._renderData.geometryPrimitives.some(({ primitive }) => primitive.show);
        return isAlignmentVisible || isAnyPrimitiveVisible;
    }
    getGeometry() {
        return this._roadGeometry.geometry;
    }
    getLength() {
        return this._alignment.length;
    }
    getLengthOnDatum() {
        return this._alignment.lengthOnDatum;
    }
    getControlPointIndex(id) {
        return this._alignment.ipLinePoints.findIndex((p) => p.id === id);
    }
    getControlPointPos(id) {
        const ipPointInfo = this._alignment.ipLinePoints.find((p) => p.id === id);
        return ipPointInfo?.localPoint.clone();
    }
    getPathPoints() {
        return this._alignment.centerLinePoints.map((p) => p.localPointWithAdjustedHeight);
    }
    getSlopeBreakPointAndHeight(slopeBreakIndex) {
        return {
            point: this._verticalAlignmentData[slopeBreakIndex].pointOnPath,
            height: this._verticalAlignmentData[slopeBreakIndex].elevation,
        };
    }
    getCorrespondingProjectedDistFromStart(distanceFromStart) {
        return this._alignment.getCorrProjectedDistanceFromStartPoint(distanceFromStart);
    }
    getCorrespondingDistanceFromStart(projectedDistanceFromStart) {
        return this._alignment.getCorrDistanceFromStartPoint(projectedDistanceFromStart);
    }
    getClosestPointInfoOnPath(point) {
        return this._alignment.getClosestPointInfoOnPath(point);
    }
    getPointInfoAtDistance(distanceFromStart_3d) {
        return this._alignment.getPointInfoAt3dDistance(distanceFromStart_3d);
    }
    getSectionOrientationAt3dDistance(distanceFromStart) {
        return this._alignment.getSectionOrientationAt3dDistance(distanceFromStart);
    }
    getCrossSectionPathPoints(distanceFromStart) {
        const data = this.getRoadCrossSectionViewData(distanceFromStart);
        return [
            ...data.pathPoints,
            ...data.pointCloudOutlines.flat(),
            ...data.designDataOutlines.filter(Boolean).flat(),
            ...data.referenceTopologyOutlines.filter(Boolean).flat(),
        ];
    }
    /**
     * Is control point removable
     * @param controlPointId Id of the control point
     * @returns Returns true if the control point is removable and in all other cases returns false
     */
    isControlPointRemovable(controlPointId) {
        return (this._alignment.ipLinePoints[0].id !== controlPointId &&
            this._alignment.ipLinePoints[this._alignment.ipLinePoints.length - 1]
                .id !== controlPointId);
    }
    // Removes the path from viewer. Clears all the data
    dispose() {
        this._roadGeometry.dispose();
        this._roadGeometry = undefined;
        this._alignment.dispose();
        this._id = "";
        this._roadSettings = undefined;
        this.disposeRenderedData();
    }
    async updateRoadGeometry() {
        this.updateConnections();
        await this.recomputeGeometry();
        await this._postProcessRoadConstruction();
        await this.render();
    }
    async initializeRoadFromAlignmentData(surveyPoints, ipPoints, horizontalAlignmentData_road, verticalAlignmentData, stationInterval) {
        const ipPointIds = await this._cesiumRenderableAlignment.initializeFromAlignmentData(surveyPoints, ipPoints, horizontalAlignmentData_road, verticalAlignmentData, stationInterval);
        await this.recomputeGeometry();
        await this._postProcessRoadConstruction();
        await this.render();
        return ipPointIds;
    }
    pickEntity(entity) {
        const result = { type: undefined, entity: undefined };
        const data = this._alignment.ipLinePoints.find((p) => p.id === entity.id);
        if (data) {
            result.type = CESIUM_PICKABLE_ENTITY.ROAD_CONTROL_POINT;
            result.entity = this._cesiumRenderableAlignment.getIPSphereEntity(data.id);
        }
        else {
            if (entity.name === CESIUM_PICKABLE_ENTITY.ROAD_PATH &&
                entity.id === this.id) {
                result.type = CESIUM_PICKABLE_ENTITY.ROAD_PATH;
                result.entity = entity;
            }
        }
        return result;
    }
    // -----------------Public methods for editing----------------
    /**
     * Adds point a the end
     * @param pointInfo
     * @param options deferRender を指定すると建設中の 1 点ごとの再描画を抑止する
     * @returns
     */
    async addIPPointAtEnd(pointInfo, options) {
        return await this.addIPPointAtIndex(this._alignment.ipLinePoints.length, {
            wgs84Position: pointInfo.position,
            localPosition: pointInfo.localPosition,
        }, options);
    }
    /**
     * This function will calculate MidControlPoint and will update this point ipPointList at perticular index
     * @param ipPointId IPPointID on which this function is called
     * @param viewer Cesium Viewer
     * @returns Newly added control point ID
     */
    async addIPPointOnRoad(ipPointId) {
        // Calculating midpoint between the current controlPoint and previous or post control point depending on the situation.
        const index = this._alignment.ipLinePoints.findIndex((ipPoint) => ipPoint.id === ipPointId);
        if (index === -1) {
            return;
        }
        const currentPoint = this._cesiumRenderableAlignment.ipLinePoints[index].wgs84Point;
        const neighborPoint = index === 0
            ? this._cesiumRenderableAlignment.ipLinePoints[index + 1].wgs84Point
            : this._cesiumRenderableAlignment.ipLinePoints[index - 1].wgs84Point;
        const newMidPoint = Cartesian3.midpoint(currentPoint, neighborPoint, new Cartesian3());
        return await this.addIPPointAtIndex(index === 0 ? index + 1 : index, {
            wgs84Position: newMidPoint,
        });
    }
    /**
     * This function will add control point at index will also update controlPointBufferMap and updateRoadGeometry()
     * @param index INDEX at which this function point will get added
     * @param id ID of control point
     * @param localPos POSITION of control point in local space
     * @param viewer Cesium Viewer
     */
    async addIPPointAtIndex(index, pointInfo, options) {
        const ipPointId = await this._cesiumRenderableAlignment.addIPPointAtIndex(index, pointInfo);
        await this._afterIPPointAdded(options);
        return ipPointId;
    }
    /**
     * Return a copy (array and elements) of the current vertical alignment data, for restoring later.
     */
    getPviSnapshot() {
        return this._alignment.verticalAlignment.verticalAlignmentData.map((v) => ({
            ...v,
        }));
    }
    /**
     * Undo-only: re-insert a deleted IP point with its original ID and replace the vertical alignment with the given pre-delete snapshot.
     * The snapshot is copied here, so it stays intact across repeated undo/redo.
     * Only for a deleted middle IP point; at first/last index `ipId` is ignored and a new ID is issued.
     */
    async restoreControlPointAtIndex(index, localPosition, ipId, pviSnapshot) {
        await this._cesiumRenderableAlignment.addIPPointAtIndex(index, { localPosition }, ipId);
        this._alignment.updateVerticalAlignmentData(pviSnapshot.map((v) => ({ ...v })));
        await this._afterIPPointAdded();
    }
    /** Post-processing after an IP point is inserted: render or recompute geometry by construction state, then log. */
    async _afterIPPointAdded(options) {
        if (this._constructionState === ConstructionState.UNDER_CONSTRUCTION) {
            // 手動作画は options 未指定なので従来どおり 1 点ごとに描画する。
            if (!options?.deferRender) {
                await this._cesiumRenderableAlignment.render();
            }
        }
        else {
            const site = Site.getInstance();
            site.structureManager.reComputeConnectionNodesConnectedToChildStructure(this._id);
            await this.updateRoadGeometry();
            //for children
            const structure = site.structureManager.getStructure(this._id);
            structure.updateConnectionOnAddUpdatePoint();
            site.structureManager.reComputeChildrenGeometry(this._id);
        }
        ActivityLogger.log(Activity.ROAD_CONTROLPOINT_UPDATED);
    }
    async updateControlPointPosition(pointInfo, clickedControlPointID) {
        const site = Site.getInstance();
        const ipPointData = this._alignment.ipLinePoints.find((p) => p.id === clickedControlPointID);
        if (!ipPointData) {
            throw new Error("Control point not found");
        }
        const oldPointInfo = {
            position: null, //ipPointData.wgs84Point.clone(),
            info: null, //ipPointData.pointInfo,
            localPosition: ipPointData.localPoint.clone(),
        };
        await this._cesiumRenderableAlignment.updateIPPointPosition(pointInfo, clickedControlPointID);
        site.structureManager.reComputeConnectionNodesConnectedToChildStructure(this._id);
        await this.updateRoadGeometry();
        //for children
        const structure = site.structureManager.getStructure(this._id);
        structure.updateConnectionOnAddUpdatePoint();
        site.structureManager.reComputeChildrenGeometry(this._id);
        ActivityLogger.log(Activity.ROAD_CONTROLPOINT_UPDATED);
        return oldPointInfo;
    }
    async removeControlPoint(clickedControlPointID) {
        const site = Site.getInstance();
        await this._cesiumRenderableAlignment.removeIPPoint(clickedControlPointID);
        if (this._constructionState === ConstructionState.UNDER_CONSTRUCTION) {
            await this._cesiumRenderableAlignment.render();
        }
        else {
            site.structureManager.reComputeConnectionNodesConnectedToChildStructure(this._id);
            await this.updateRoadGeometry();
            //for children
            const structure = site.structureManager.getStructure(this._id);
            const nodesToDelete = structure.updateConnectionOnDeletePoint();
            site.structureManager.reComputeChildrenGeometry(this._id);
            for (const nodeTodelete of nodesToDelete) {
                const childStructureOfNode = site.structureManager.getStructure(nodeTodelete.to.id);
                if (childStructureOfNode) {
                    childStructureOfNode.updateGeometry();
                    childStructureOfNode.updateConnectionOnAddUpdatePoint();
                }
            }
        }
        ActivityLogger.log(Activity.ROAD_CONTROLPOINT_UPDATED);
    }
    async updateSlopeBreak(slopeBreaks, updateViewer = true) {
        const length = this._alignment.lengthOnDatum;
        const vaData = slopeBreaks
            .filter((v) => v.isActive)
            .map((v) => ({
            elevation: v.height,
            horizontalDistance: v.percentage * length,
        }));
        this._alignment.updateVerticalAlignmentData(vaData);
        if (!updateViewer) {
            ActivityLogger.log(Activity.ROAD_SLOPEBREAK_UPDATED);
            return;
        }
        await this.recomputeGeometry();
        await this._postProcessRoadConstruction();
        await this.render();
        ActivityLogger.log(Activity.ROAD_SLOPEBREAK_UPDATED);
    }
    //road can be either made completely straight or set to default (combination of straight, clothoid/quadratic, arc)
    async setTypeToHorizontalAlignment(type) {
        if (this._alignment.horizontalAlignment.type === type) {
            return;
        }
        this._alignment.setTypeToHorizontalAlignment(type);
        await this.recomputeGeometry();
        await this._postProcessRoadConstruction();
        await this.render();
    }
    async updatePathSettings(settings) {
        this._roadSettings = settings;
        await this.updateRoadGeometry();
        ActivityLogger.log(Activity.ROAD_SETTINGS_UPDATED);
    }
    updateMaterial(commonMaterial, embarkmentMaterial, cutMaterial) {
        if (commonMaterial.surfaceColor) {
            this._roadSettings.commonSetting.path.color = commonMaterial.surfaceColor;
        }
        if (embarkmentMaterial.color) {
            this._roadSettings.commonSetting.embarkmentSetting.color =
                embarkmentMaterial.color;
        }
        if (embarkmentMaterial.transparency !== undefined) {
            this._roadSettings.commonSetting.embarkmentSetting.transparency =
                embarkmentMaterial.transparency;
        }
        if (cutMaterial.color) {
            this._roadSettings.commonSetting.cutSetting.color = cutMaterial.color;
        }
        if (cutMaterial.transparency) {
            this._roadSettings.commonSetting.cutSetting.transparency =
                cutMaterial.transparency;
        }
        this.renderGeometry();
    }
    async updateAssociatedRubbingDestination(rubbingDestination) {
        this._rubbingDestination = rubbingDestination;
        await this.updateRoadGeometry();
    }
    updateAssociatedTerrain(terrain) {
        this._associatedTerrain = terrain;
    }
    updateLabelColors(color) {
        this._cesiumRenderableAlignment.renderIPPointLabels(color);
    }
    setAlignmentVisibility(show) {
        //center line
        this._cesiumRenderableAlignment.setVisibility(show);
        //pvi points
        this._renderData.pviSpheres.forEach((value) => {
            value.show = show;
        });
    }
    setRoadVisibility(show) {
        this.setIpLineVisibility(show);
        if (!show) {
            this.setCenterLineVisibility(false);
        }
        if (this._renderData.geometryPrimitives) {
            this._renderData.geometryPrimitives.forEach(({ primitive }) => {
                primitive.show = show;
            });
        }
    }
    setIpLineVisibility(visible) {
        this._cesiumRenderableAlignment.setIpLineVisibility(visible);
    }
    get isCenterLineVisible() {
        return this._cesiumRenderableAlignment.isCenterLineVisible;
    }
    setCenterLineVisibility(visible) {
        this._cesiumRenderableAlignment.setCenterLineVisibility(visible);
        this._renderData.pviSpheres.forEach((value) => {
            value.show = visible;
        });
    }
    setSurfaceVisibility(show) {
        this._renderData.geometryPrimitives
            .filter(({ type }) => type === EmbankmentType.NONE)
            .forEach(({ primitive }) => {
            primitive.show = show;
        });
    }
    setEmbankmentVisibility(show) {
        this.setFillEmbankmentVisibility(show);
        this.setCutEmbankmentVisibility(show);
    }
    setFillEmbankmentVisibility(show) {
        this._renderData.geometryPrimitives
            .filter(({ type }) => type === EmbankmentType.FILL)
            .forEach(({ primitive }) => {
            primitive.show = show;
        });
    }
    setCutEmbankmentVisibility(show) {
        this._renderData.geometryPrimitives
            .filter(({ type }) => type === EmbankmentType.CUT)
            .forEach(({ primitive }) => {
            primitive.show = show;
        });
    }
    get2DViewerData({ distanceFromStart = 0, }) {
        const site = Site.getInstance();
        let updatePointCloud = false;
        let updateDesignData = false;
        let updateCrossSection = false;
        let updateReferenceTopology = false;
        let updateAll = false;
        let activity = ActivityLogger.latest;
        while (activity) {
            let breakwhile = false;
            switch (activity.activity) {
                case Activity.ROAD_2D_DATA_CALCULATED:
                    breakwhile = true;
                    break;
                //pointcloud-outlines,
                case Activity.POINTCLOUD_LOADED:
                case Activity.POINT_CLOUD_VISIBILITY_UPDATE:
                    updatePointCloud = true;
                    break;
                //designdata-outlines
                case Activity.DESIGNDATA_LOADED:
                case Activity.DESIGNDATA_VISIBILITY_UPDATE:
                    updateDesignData = true;
                    break;
                case Activity.REFERENCE_TOPOLOGY_LOADED:
                case Activity.REFERENCE_TOPOLOGY_VISIBILITY_UPDATE:
                case Activity.REFERENCE_TOPOLOGY_EDITED:
                    updateReferenceTopology = true;
                    break;
                case Activity.ROAD_LOADED: //all
                case Activity.ROAD_EDIT_STARTED: //all
                case Activity.ROAD_CONTROLPOINT_UPDATED: // all
                case Activity.ROAD_GEOMETRYSHAPE_UPDATED: // all
                case Activity.ROAD_SLOPEBREAK_UPDATED: // cross-section
                case Activity.ROAD_SETTINGS_UPDATED: // cross-section
                    updateAll = true;
                    breakwhile = true;
                    break;
                case Activity.ROAD_SECTION_PLANE_UPDATED: // cross-section
                    updateCrossSection = true;
                    break;
            }
            if (breakwhile)
                break;
            activity = activity.previous;
        }
        if (this._2dDataMemo.distanceFromStart !== distanceFromStart) {
            updateCrossSection = true;
            this._2dDataMemo.distanceFromStart = distanceFromStart;
        }
        this._2dDataMemo.altitudeViewData.slopeBreaks =
            this._verticalAlignmentData.map((va) => ({
                percentage: va.horizontalDistance / this._alignment.lengthOnDatum,
                height: va.elevation,
                isActive: va.isActive,
                associatedControlPointId: va.associatedIPPointId,
            }));
        const pointsForTracingAlongPath = this._prepareTracePoints(DEFAULT_TERRIAN_PROFILE_VIEW_STEP_SIZE);
        const updatePathLineData = () => {
            this._2dDataMemo.altitudeViewData.pathPoints =
                this._createOutlineForRoad();
            this._2dDataMemo.altitudeViewData.sectionMarkers = toSectionMarkers(this._alignment.centerLinePoints);
        };
        const updateCrossSectionRelatedData = () => {
            this._2dDataMemo.altitudeViewData.sectionLineDistFromFirstPoint =
                this._2dDataMemo.altitudeViewData.pathPoints[0][0] +
                    this.getCorrespondingProjectedDistFromStart(distanceFromStart);
            const sectionViewData = this.getRoadCrossSectionViewData(this._2dDataMemo.altitudeViewData.sectionLineDistFromFirstPoint);
            this._2dDataMemo.sectionViewData.pathPoints = sectionViewData.pathPoints;
            this._2dDataMemo.sectionViewData.pointCloudOutlines =
                sectionViewData.pointCloudOutlines;
            this._2dDataMemo.sectionViewData.designDataOutlines =
                sectionViewData.designDataOutlines;
            this._2dDataMemo.sectionViewData.referenceTopologyOutlines =
                sectionViewData.referenceTopologyOutlines;
            this._2dDataMemo.sectionViewData.referencePointIndex =
                sectionViewData.referencePointIndex;
        };
        const updatePointCloudRelatedData = () => {
            this._2dDataMemo.altitudeViewData.sectionLineDistFromFirstPoint =
                this._2dDataMemo.altitudeViewData.pathPoints[0][0] +
                    this.getCorrespondingProjectedDistFromStart(distanceFromStart);
            this._2dDataMemo.altitudeViewData.slopeBreaks =
                this._verticalAlignmentData.map((va) => ({
                    percentage: va.horizontalDistance / this._alignment.lengthOnDatum,
                    height: va.elevation,
                    isActive: va.isActive,
                    associatedControlPointId: va.associatedIPPointId,
                }));
            const road_pointCloudOutlines = [];
            site.getPointClouds().forEach((data) => {
                if (data.tileSet.show) {
                    const { outline } = this._createOutlineForTileset(pointsForTracingAlongPath, data);
                    road_pointCloudOutlines.push(outline);
                }
                this._2dDataMemo.altitudeViewData.pointCloudOutlines =
                    road_pointCloudOutlines;
            });
        };
        const updateDesignDataRelatedData = () => {
            const road_designDataOutlines = [];
            site.getDesignDatas().forEach((data) => {
                if (data.tileSet.show) {
                    const { outline } = this._createOutlineForTileset(pointsForTracingAlongPath, data);
                    road_designDataOutlines.push(outline);
                }
            });
            this._2dDataMemo.altitudeViewData.designDataOutlines =
                road_designDataOutlines;
        };
        const updateReferenceTopologyRelatedData = () => {
            const road_referenceTopologyOutlines = [];
            site.getReferenceTopologies().forEach((data) => {
                if (data.getDrawing2DImage().isVisible()) {
                    const { outline } = this._createOutlineForTileset(pointsForTracingAlongPath, data);
                    road_referenceTopologyOutlines.push(outline);
                }
            });
            this._2dDataMemo.altitudeViewData.referenceTopologyOutlines =
                road_referenceTopologyOutlines;
        };
        updatePathLineData();
        if (updateAll) {
            updatePointCloudRelatedData();
            updateDesignDataRelatedData();
            updateCrossSectionRelatedData();
            updateReferenceTopologyRelatedData();
        }
        else if (updatePointCloud) {
            updatePointCloudRelatedData();
            updateCrossSectionRelatedData();
        }
        else if (updateDesignData) {
            updateDesignDataRelatedData();
            updateCrossSectionRelatedData();
        }
        else if (updateReferenceTopology) {
            updateReferenceTopologyRelatedData();
            updateCrossSectionRelatedData();
        }
        else if (updateCrossSection) {
            updateCrossSectionRelatedData();
        }
        ActivityLogger.log(Activity.ROAD_2D_DATA_CALCULATED);
        return this._2dDataMemo;
    }
    getLinearData(roadObj) {
        try {
            const controlPoints = this._alignment.ipLinePoints.map((p) => p.localPoint);
            const controlPointArray = [];
            for (let i = 0; i < controlPoints.length; i++) {
                let pointType;
                if (i === 0) {
                    pointType = PointType.BEGINNING_POINT;
                }
                else if (i === controlPoints.length - 1) {
                    pointType = PointType.END_POINT;
                }
                else {
                    pointType = PointType.INTERMEDIATE_POINT;
                }
                const controlPoint = {
                    point: [controlPoints[i].x, controlPoints[i].y, controlPoints[i].z],
                    pointType: pointType,
                };
                controlPointArray.push(controlPoint);
            }
            const arbitraryLineJSON = {
                version: 2.0,
                lineId: this._id,
                controlPoints: controlPointArray,
                associatedTerrain: {
                    id: this.associatedTerrain.id,
                    terrainType: this.associatedTerrain.assetType,
                },
            };
            const roadLinearInfo = {
                version: 2.0,
                lineInfo: {
                    id: this._id,
                    label: roadObj.roadName,
                    json: arbitraryLineJSON,
                    siteId: roadObj.siteID,
                    isVisible: roadObj.isVisible,
                },
            };
            return JSON.stringify(roadLinearInfo);
        }
        catch (error) {
            //To do: implement mechanism to notify user.
            console.warn(error);
        }
    }
    getPositionForSectionPlane() {
        const position = [];
        const firstPoint = Cartographic.fromCartesian(this.getPathPoints()[0]);
        const secondPoint = Cartographic.fromCartesian(this.getPathPoints()[1]);
        let dist = EarthBrainUtils.surfaceDistanceBetweenTwoPoints(firstPoint, secondPoint);
        dist = dist * 10;
        const dir = new Cartographic(secondPoint.longitude - firstPoint.longitude, secondPoint.latitude - firstPoint.latitude, secondPoint.height - firstPoint.height);
        const finalSecondPoint = Cartesian3.fromRadians(firstPoint.longitude + dir.longitude / dist, firstPoint.latitude + dir.latitude / dist, firstPoint.height);
        position.push(Cartesian3.fromRadians(firstPoint.longitude, firstPoint.latitude, firstPoint.height), finalSecondPoint);
        return position;
    }
    getRoadCrossSectionViewData(distanceFromStartPoint) {
        const site = Site.getInstance();
        const result = {
            pathPoints: [],
            pointCloudOutlines: [],
            designDataOutlines: [],
            referenceTopologyOutlines: [],
            referencePointIndex: -1,
        };
        const orientation = this.getSectionOrientationAt3dDistance(distanceFromStartPoint);
        if (!orientation) {
            return result;
        }
        const { pointOnPath, direction } = orientation;
        const { left, right } = this._roadGeometry.pathGeometry.getSidePoints(pointOnPath, direction);
        const sortBy2DDistanceFromStartPoint = (points, start) => {
            points.sort((a, b) => Cartesian2.distance(start, a) - Cartesian2.distance(start, b)); // Assending Order
        };
        const zBoundedPlane = ZBoundedPlane.fromPointNormal(pointOnPath, 
        // ZBoundedPlane requires a normal parallel to the xy plane
        Cartesian3.normalize(new Cartesian3(direction.forward.x, direction.forward.y, 0), new Cartesian3())); // no bounds as we need whole of road
        const intersectedPoints = intersectGeometryByPlane(this._roadGeometry.geometry, zBoundedPlane);
        const leftFacingPlane = ZBoundedPlane.fromPointNormal(left, direction.left);
        const rightFacingPlane = ZBoundedPlane.fromPointNormal(right, direction.right);
        const leftPoints = intersectedPoints.filter((p) => leftFacingPlane.raycast(new Ray(p, direction.right)));
        const rightPoints = intersectedPoints.filter((p) => rightFacingPlane.raycast(new Ray(p, direction.left)));
        sortBy2DDistanceFromStartPoint(leftPoints, pointOnPath);
        sortBy2DDistanceFromStartPoint(rightPoints, pointOnPath);
        leftPoints.unshift(left);
        rightPoints.unshift(right);
        const filteredLeftPoints = [];
        for (const point of leftPoints) {
            if (!filteredLeftPoints[filteredLeftPoints.length - 1] ||
                Cartesian3.distance(filteredLeftPoints[filteredLeftPoints.length - 1], point) > 0.00000001) {
                filteredLeftPoints.push(point);
            }
        }
        const filteredRightPoints = [];
        for (const point of rightPoints) {
            if (!filteredRightPoints[filteredRightPoints.length - 1] ||
                Cartesian3.distance(filteredRightPoints[filteredRightPoints.length - 1], point) > 0.00000001) {
                filteredRightPoints.push(point);
            }
        }
        filteredLeftPoints.reverse();
        const points3d = [
            ...filteredLeftPoints,
            pointOnPath,
            ...filteredRightPoints,
        ];
        const points2d = [];
        for (const point of points3d) {
            const translatedPoint = Cartesian3.subtract(point, pointOnPath, new Cartesian3());
            const dist = Cartesian3.dot(direction.right, translatedPoint);
            points2d.push([dist, point.z]);
        }
        const cesium2List = points2d.map((p) => Cartesian2.fromArray(p));
        const box = BoundingRectangle.fromPoints(cesium2List);
        // We should be considering 1.5 times the bottom road width in each direction
        // meaning 3 times the bottom width of the road
        const terrianLineRegion = 2 * box.width;
        // Step is defined as 0.5 meters
        const step = DEFAULT_TERRIAN_SECTION_VIEW_STEP_SIZE;
        const pointCountOnEachSide = Math.floor(terrianLineRegion / (2 * step));
        const roadOutlineCartesians = [];
        roadOutlineCartesians.push(pointOnPath);
        let i = 1;
        while (i < pointCountOnEachSide) {
            const pLeft = new Cartesian3();
            Cartesian3.multiplyByScalar(direction.left, i * step, pLeft);
            Cartesian3.add(pLeft, pointOnPath, pLeft);
            roadOutlineCartesians.unshift(pLeft);
            const pRight = new Cartesian3();
            Cartesian3.multiplyByScalar(direction.right, i * step, pRight);
            Cartesian3.add(pRight, pointOnPath, pRight);
            roadOutlineCartesians.push(pRight);
            ++i;
        }
        // The 2D projected coordinates for the road are referenced to the center of the road
        // Since the outlines are referenced to the leftmost point we need to align them with the center
        // Hence translating in x by center point on the outline
        const prepareOutline = (points, tileset) => {
            const { offsetToCenter, outline } = this._createOutlineForTileset(points.map((p) => ({
                pathPoint: p,
                pathPointOnEllipsoid: new Cartesian3(p.x, p.y, 0),
            })), tileset);
            for (const point of outline) {
                point[0] = point[0] - offsetToCenter;
            }
            return outline;
        };
        site.getPointClouds().forEach((data) => {
            if (data.tileSet.show) {
                result.pointCloudOutlines.push(prepareOutline(roadOutlineCartesians, data));
            }
        });
        result.designDataOutlines = site.getDesignDatas().map((d) => {
            if (d.tileSet.show) {
                return prepareOutline(roadOutlineCartesians, d);
            }
        });
        result.referenceTopologyOutlines = site
            .getReferenceTopologies()
            .map((d) => {
            if (d.getDrawing2DImage().isVisible()) {
                return prepareOutline(roadOutlineCartesians, d);
            }
        });
        // This point from road points array will be used to calculate the measurements for the road
        result.pathPoints = points2d;
        result.referencePointIndex = result.pathPoints.findIndex((p) => p[0] === 0);
        return result;
    }
    fitToRoad() {
        const boundingSphere = this._cesiumRenderableAlignment.boundingSphere;
        this._viewer.camera.flyToBoundingSphere(boundingSphere, {
            offset: new HeadingPitchRange(CMath.toRadians(0.0), CMath.toRadians(-30.0), 0.0),
        });
        this.isMagnified = true;
    }
    rayCast(ray) {
        return EarthBrainUtils.intersectAgainstIGeometry(ray, this.getGeometry());
    }
    tracePoints(points) {
        return EarthBrainUtils.tracePointsOnGeometry(this._roadGeometry.geometry, this.heightMap, points);
    }
    getHeightInfo() {
        return this._alignment.verticalAlignment.getHeightRange();
    }
    getSlopeInfo() {
        return this._alignment.verticalAlignment.getSlopeRange();
    }
    toJSON() {
        try {
            const { horizontalAlignment, verticalAlignment, surveyPoints, ipPointIds, } = this._cesiumRenderableAlignment.toJSON();
            const roadInfo = {
                roadId: this._id,
                roadSettings: this._roadSettings,
                associatedTerrain: {
                    id: this._associatedTerrain.id,
                    terrainType: this._associatedTerrain.assetType,
                },
                surveyPoints,
                ipPointIds,
                rubbingDestination: this._rubbingDestination,
                horizontalAlignment: {
                    type: this._alignment.horizontalAlignment.type,
                    data: horizontalAlignment,
                },
                verticalAlignment: verticalAlignment,
            };
            return roadInfo;
        }
        catch (error) {
            console.error("Error serializing road:", error);
            throw error;
        }
    }
    /**
     * Calculates and returns the axis-aligned bounding box (AABB) for the road.
     * The AABB is computed based on the WGS84 coordinates of the control points and geometry vertices.
     * If no points are available, an empty AABB is returned.
     *
     * NOTE: using heighmap AABB or chaching AABB will enhance performance.
     *
     * @returns {AxisAlignedBoundingBox} The AABB of the road.
     */
    roadAABB_WGS84() {
        const geometryVerticesWGS84 = [];
        for (const geomData of this._wgs84dataMemo.geometries) {
            if (geomData.geometry.vertices.length > 0) {
                const unpackedVertices = Cartesian3.unpackArray(geomData.geometry.vertices);
                geometryVerticesWGS84.push(...unpackedVertices);
            }
        }
        if (geometryVerticesWGS84.length === 0) {
            return new AxisAlignedBoundingBox();
        }
        return AxisAlignedBoundingBox.fromPoints(geometryVerticesWGS84);
    }
    /** Recompute centerline at interpolation pitch 5 and return local positions (road settings/geometry untouched) */
    async recalculateCenterLinePositionsCore() {
        // --- Copy settings with overridden interpolation pitch
        const { commonSetting } = this._roadSettings;
        const roadSetting = {
            ...this._roadSettings,
            commonSetting: {
                ...commonSetting,
                path: { ...commonSetting.path, interpolationPitch: 5 },
            },
        };
        // --- Recompute centerline path only (no side slopes)
        const roadGeometry = new RoadGeometry(this.id);
        roadGeometry.update(this._alignment, roadSetting);
        const positions = roadGeometry.interpolatedPathInfos.map((info) => info.position.clone());
        roadGeometry.dispose();
        return positions;
    }
    getSlopeBreakInfo(index) {
        const road2DLength = this._alignment.lengthOnDatum;
        const normalizedDistance = this._verticalAlignmentData[index].horizontalDistance / road2DLength;
        const length3dToThisPoint = this.getCorrespondingDistanceFromStart(this._verticalAlignmentData[index].horizontalDistance);
        const associatedControlPoint = this._alignment.ipLinePoints.find((p) => p.id === this._verticalAlignmentData[index].associatedIPPointId);
        const slopeBreakInfo = {
            percentage: normalizedDistance,
            height: this._verticalAlignmentData[index].elevation,
            pointOnPath: this._verticalAlignmentData[index].pointOnPath,
            length2d: this._verticalAlignmentData[index].horizontalDistance,
            length3d: length3dToThisPoint,
            index: index,
            isEnabled: this._verticalAlignmentData[index].isActive,
            controlPoint: associatedControlPoint
                ? {
                    name: associatedControlPoint.label,
                    point: associatedControlPoint.localPoint, //heightAdjustedLocalPosition
                }
                : undefined,
            associatedControlPointId: associatedControlPoint?.id,
        };
        return slopeBreakInfo;
    }
    getIndexOfPointOnIPPath(point) {
        return this._alignment.getIndexOfPointOnIPPath(point);
    }
    // ------------------Private methods------------------
    updateConnections() {
        const site = Site.getInstance();
        //check if it depends on others -> add those dependents as intersection data
        const localControlPoints = this._alignment.ipLinePoints.map((p) => ({
            point: p.localPointWithAdjustedHeight,
        }));
        const parentNodes = site.structureManager.getParentsNodes(this._id); //parentNodes can be at max 2
        for (const parentNode of parentNodes) {
            const parentStructure = site.structureManager.getStructure(parentNode.from.id);
            const pointInfo = parentStructure.getPointInfoAtProportionIntotal(parentNode.from.proportionInTotal);
            pointInfo.pointOnPath.clone(localControlPoints[parentNode.to.controlPointIndex].point);
        }
    }
    async recomputeGeometry() {
        const site = Site.getInstance();
        const rubbingDestination = getRaycastableFromRubbingDestination(site, this._rubbingDestination);
        this._roadGeometry.update(this._alignment, this._roadSettings, rubbingDestination);
        const roadSegGeometries = this._roadGeometry.getMergedGeometriesBySegmentSubType();
        // Create HeightMap
        this._heightMap = undefined;
        if (this._roadGeometry.geometry.vertices.length > 0) {
            this._heightMap = HeightMap.FromIndexedGeometry(this._roadGeometry.geometry, OBJECT_CELL_SIZE);
        }
        await this._updateWGS84Data(roadSegGeometries);
    }
    // vertical alignment data should include all ip points and vertical intersections
    async updateVerticalAlignmentDataForRoad() {
        const verticalAlignmentData = [];
        const ipDistances = [];
        this._alignment.ipLinePoints.forEach((ip) => {
            const closestPointOnPath = this._alignment.getClosestPointInfoOnPath(ip.localPoint);
            verticalAlignmentData.push({
                horizontalDistance: closestPointOnPath.distanceFromStart_2D,
                elevation: closestPointOnPath.point.z,
                associatedIPPointId: ip.id,
                pointOnPath: closestPointOnPath.point,
                isActive: false,
                wgs84PointOnPath: null,
            });
            ipDistances.push({
                ipId: ip.id,
                horizontalDistance: closestPointOnPath.distanceFromStart_2D,
            });
        });
        const links = linkPvisToIps(this._alignment.verticalAlignment.verticalAlignmentData, ipDistances);
        for (const link of links) {
            if (link.kind === "ip") {
                const vaData = verticalAlignmentData.find((v) => v.associatedIPPointId === link.ipId);
                if (vaData) {
                    vaData.isActive = true;
                    vaData.elevation = link.pvi.elevation;
                }
            }
            else {
                const pointOnPath = this._alignment.getPointInfoAt2dDistance(link.pvi.horizontalDistance);
                verticalAlignmentData.push({
                    horizontalDistance: link.pvi.horizontalDistance,
                    elevation: pointOnPath.z,
                    pointOnPath: pointOnPath,
                    isActive: true,
                    wgs84PointOnPath: null,
                });
            }
        }
        verticalAlignmentData.sort((a, b) => a.horizontalDistance - b.horizontalDistance);
        const localPoints = verticalAlignmentData.map((pvi) => pvi.pointOnPath);
        const wgs84Points = await Site.getInstance().convertPointsToWGS84(localPoints);
        verticalAlignmentData.forEach((pvi, index) => {
            pvi.wgs84PointOnPath = wgs84Points[index];
        });
        this._verticalAlignmentData = verticalAlignmentData;
    }
    /**
     * Creates a list of points as outline for 2D view for a tileset.
     * Plots distance between points on X
     * Plots the point height / terrain height on Y
     * @param viewer Viewer
     * @param pathArray Points of the tileset
     * @param tileset tileset on which points need to be plotted
     * @returns
     */
    _createOutlineForTileset(pathArray, tileset) {
        const centerIndex = Math.floor(pathArray.length / 2);
        let offsetToCenter = 0;
        const outline = [];
        let xDistFromStart = 0;
        for (let i = 0; i < pathArray.length; ++i) {
            if (pathArray[i - 1]) {
                xDistFromStart += Cartesian3.distance(pathArray[i - 1].pathPointOnEllipsoid, pathArray[i].pathPointOnEllipsoid);
            }
            if (i === centerIndex) {
                offsetToCenter = xDistFromStart;
            }
            const projectHeight = tileset.getProjectPointHeight(pathArray[i].pathPoint);
            if (projectHeight) {
                outline.push([xDistFromStart, projectHeight]);
            }
        }
        return { offsetToCenter, outline };
    }
    /**
     * Creates a list of points as outline for 2D view for a road.
     * Plots distance between points (Angular distance) on X
     * Plots the point height on Y
     * @param roadPoints Points of the road
     * @returns
     */
    _createOutlineForRoad() {
        const roadPoints = this._alignment.centerLinePoints;
        const outline = [];
        let dist = 0;
        let cartDistance = 0;
        for (let i = 0; i < roadPoints.length; ++i) {
            if (roadPoints[i - 1]) {
                dist += Cartesian2.distance(roadPoints[i - 1].localPointWithAdjustedHeight, roadPoints[i].localPointWithAdjustedHeight);
                cartDistance += Cartesian3.distance(roadPoints[i - 1].localPointWithAdjustedHeight, roadPoints[i].localPointWithAdjustedHeight);
            }
            const height = roadPoints[i].localPointWithAdjustedHeight.z;
            outline.push([dist, height, cartDistance]);
        }
        return outline;
    }
    //ToDo-2965: Is this not same as linear interpolation? Should we remove this function?
    // It is linear interpolation but should happen in 2d
    _interpolateIn2dBetweenTwoPoints(startPoint, endPoint, step, includeStart, includeEnd) {
        const result = [];
        if (Cartesian2.equals(startPoint, endPoint)) {
            console.warn(`Start and End point for are same. Please check the data for road ${this.id}.`);
        }
        else {
            const dir = EarthBrainUtils.getNormalizedDirection(startPoint, endPoint);
            const horDistBetweenPoint = Cartesian2.distance(startPoint, endPoint);
            const count = Math.floor(horDistBetweenPoint / step);
            const distBetweenPoint = Cartesian3.distance(startPoint, endPoint);
            const stepAlongSlope = (step / horDistBetweenPoint) * distBetweenPoint;
            includeStart && result.push(startPoint);
            let i = 1;
            while (i < count) {
                const pointAtStep = new Cartesian3();
                Cartesian3.multiplyByScalar(dir, i * stepAlongSlope, pointAtStep);
                Cartesian3.add(pointAtStep, startPoint, pointAtStep);
                result.push(pointAtStep);
                i++;
            }
            includeEnd && result.push(endPoint);
        }
        return result;
    }
    _prepareTracePoints(stepBetweenTwoPoints) {
        const pathPoints = this._alignment.centerLinePoints;
        const roadOutlineCartesians = [];
        for (let j = 0; j < pathPoints.length - 1; j++) {
            const startPoint = pathPoints[j].localPointWithAdjustedHeight;
            const endPoint = pathPoints[j + 1].localPointWithAdjustedHeight;
            const isLastPoint = j + 1 === pathPoints.length - 1;
            const points = this._interpolateIn2dBetweenTwoPoints(startPoint, endPoint, stepBetweenTwoPoints, true, isLastPoint);
            if (roadOutlineCartesians[roadOutlineCartesians.length - 1] &&
                points[0]) {
                if (Cartesian3.equals(roadOutlineCartesians[roadOutlineCartesians.length - 1].pathPoint, points[0])) {
                    roadOutlineCartesians.pop();
                }
            }
            roadOutlineCartesians.push(...points.map((p) => ({
                pathPoint: p,
                pathPointOnEllipsoid: new Cartesian3(p.x, p.y, 0),
            })));
        }
        return roadOutlineCartesians;
    }
    async _updateWGS84Data(roadSegGeometries) {
        const site = Site.getInstance();
        // Batch all segment vertices into a single convertPointsToWGS84 call
        // to minimize Worker WebSocket queue round-trips
        const segmentVerticesArrays = roadSegGeometries.map((data) => Cartesian3.unpackArray(data.geometry.vertices));
        const allPoints = [];
        const segmentOffsets = [];
        for (const vertices of segmentVerticesArrays) {
            segmentOffsets.push({ start: allPoints.length, count: vertices.length });
            allPoints.push(...vertices);
        }
        try {
            const allConverted = await site.convertPointsToWGS84(allPoints);
            if (allConverted) {
                for (let i = 0; i < roadSegGeometries.length; i++) {
                    const { start, count } = segmentOffsets[i];
                    roadSegGeometries[i].geometry.vertices = Cartesian3.packArray(allConverted.slice(start, start + count));
                }
            }
        }
        catch (error) {
            console.warn(error);
            for (const data of roadSegGeometries) {
                data.geometry.vertices.length = 0;
            }
        }
        this._wgs84dataMemo.geometries = roadSegGeometries;
    }
    async _postProcessRoadConstruction() {
        await this.updateVerticalAlignmentDataForRoad();
        // await this.adjustHeightOfIPPoints();
        // await this.updateSurveyPoints();
    }
    getPickableTypeBySegmentType(segType) {
        switch (segType) {
            case EmbankmentType.FILL:
                return CESIUM_PICKABLE_ENTITY.ROAD_EMBARKMENT_GEOMETRY;
            case EmbankmentType.CUT:
                return CESIUM_PICKABLE_ENTITY.ROAD_CUT_GEOMETRY;
            case EmbankmentType.NONE:
                return CESIUM_PICKABLE_ENTITY.ROAD_PATH;
        }
    }
    //-----------private render helpers-----------
    getControlPointSphere(position, id) {
        const sphereEntity = EarthBrainUtils.addSphere(this._viewer, position, Color.FUCHSIA, Number.POSITIVE_INFINITY, undefined, id);
        sphereEntity.addProperty("pickInfo"); //Information when this sphere gets picked
        sphereEntity["pickInfo"] = {
            assetType: AssetType.Road_Data,
            pickableType: CESIUM_PICKABLE_ENTITY.ROAD_CONTROL_POINT,
            entity: sphereEntity,
            owner: this,
        };
        return sphereEntity;
    }
    getColorBySegmentType(segType) {
        switch (segType) {
            case EmbankmentType.FILL:
                const embankmentMaterial = Color.fromCssColorString(this._roadSettings.commonSetting.embarkmentSetting.color);
                embankmentMaterial.alpha =
                    this._roadSettings.commonSetting.embarkmentSetting.transparency;
                return embankmentMaterial;
            case EmbankmentType.CUT:
                const cutMaterial = Color.fromCssColorString(this._roadSettings.commonSetting.cutSetting.color);
                cutMaterial.alpha =
                    this._roadSettings.commonSetting.cutSetting.transparency;
                return cutMaterial;
            case EmbankmentType.NONE:
                const roadSurfaceMaterial = Color.fromCssColorString(this._roadSettings.commonSetting.path.color); //Color.fromRandom().withAlpha(1.0);
                roadSurfaceMaterial.alpha =
                    this._roadSettings.commonSetting.embarkmentSetting.transparency !==
                        undefined
                        ? this._roadSettings.commonSetting.embarkmentSetting.transparency
                        : roadSurfaceMaterial.alpha;
                return roadSurfaceMaterial;
        }
    }
    renderPVISpheres() {
        this.disposePVISpheres();
        this._verticalAlignmentData.forEach((pvi, i) => {
            if (i % (this._verticalAlignmentData.length - 1) === 0)
                return;
            const sphere = this.getControlPointSphere(pvi.wgs84PointOnPath);
            this._renderData.pviSpheres.set(sphere.id, sphere);
        });
    }
    async renderGeometry() {
        this.disposePrimitives();
        this._wgs84dataMemo.geometries.forEach((wgs84Geom) => {
            if (wgs84Geom.geometry.vertices.length > 0 &&
                wgs84Geom.geometry.vertices.length % 3 === 0) {
                const color = this.getColorBySegmentType(wgs84Geom.embankmentType);
                const pickableType = this.getPickableTypeBySegmentType(wgs84Geom.embankmentType);
                const primitive = EarthBrainUtils.createEntityFromIndexedGeometry(wgs84Geom.geometry, color, {
                    pickInfo: {
                        entity: { id: wgs84Geom.id },
                        assetType: AssetType.Road_Data,
                        pickableType,
                        owner: this,
                        structureInfo: { id: this._id },
                    },
                });
                const wireframePrimitive = EarthBrainUtils.createEntityFromIndexedGeometry(wgs84Geom.geometry, Color.BLACK, {
                    pickInfo: {
                        entity: { id: wgs84Geom.id },
                        assetType: AssetType.Road_Data,
                        pickableType,
                        owner: this,
                        structureInfo: { id: this._id },
                    },
                }, true);
                this._viewer.scene.primitives.add(primitive);
                this._viewer.scene.primitives.add(wireframePrimitive);
                this._renderData.geometryPrimitives.push({
                    primitive,
                    type: wgs84Geom.embankmentType,
                });
                this._renderData.geometryPrimitives.push({
                    primitive: wireframePrimitive,
                    type: wgs84Geom.embankmentType,
                });
            }
        });
    }
    disposePrimitives() {
        this._renderData.geometryPrimitives.forEach(({ primitive }) => {
            this._viewer.scene.primitives.remove(primitive);
            !primitive.isDestroyed() && primitive.destroy();
        });
        this._renderData.geometryPrimitives.length = 0;
    }
    disposePVISpheres() {
        this._renderData.pviSpheres.forEach((entity) => {
            this._viewer.entities.remove(entity);
        });
        this._renderData.pviSpheres.clear();
    }
    disposeRenderedData() {
        this.disposePVISpheres();
        this._cesiumRenderableAlignment.dispose();
        this.disposePrimitives();
    }
    async render() {
        // Debug Entities
        this._removeDebugEntities();
        // PVI reference points
        // this.renderPVISpheres();
        // Alignment
        await this._cesiumRenderableAlignment.render();
        // Geometry
        this.renderGeometry();
        this._viewer.scene.requestRender();
    }
    _removeDebugEntities() {
        this._debugEntities.forEach((entity) => {
            this._viewer.entities.remove(entity);
        });
        this._debugEntities = [];
    }
}
//# sourceMappingURL=earthbrain-road.js.map